/**
 * App version watcher — detects when a fresh deploy has gone out and prompts
 * the user (or auto-reloads) so they don't keep running a stale bundle.
 *
 * How it works:
 *   • At build time, `scripts/write-version.js` writes a `buildInfo.js` module
 *     into the bundle with `BUILD_ID` = the build's unique id. This is what
 *     the running JS *actually is*, regardless of how it was cached/served.
 *   • Every POLL_MS we fetch `/version.json?ts=<rand>` (cache-busted) to get
 *     the build id of the *latest deploy on the server*.
 *   • If they differ, the user is on a stale build. We emit a
 *     `opik:version-stale` event and call the registered handlers (the
 *     AppVersionNotifier renders a toast + auto-reload).
 *   • A first-load mismatch is detected immediately — important when CDNs
 *     serve cached HTML/JS for several minutes after a deploy.
 *
 * Why also fetch on `visibilitychange`?
 *   Long-running tabs (left open overnight) miss the periodic poll while
 *   inactive. As soon as the tab regains focus we re-check so users don't
 *   shop through a stale build first.
 */
import { BUILD_ID } from "./buildInfo";

const POLL_MS = 60_000;             // 60 s baseline poll
const VISIBILITY_DEBOUNCE_MS = 5_000; // throttle visibility-triggered checks
const VERSION_URL = "/version.json";

const _runningBuildId = BUILD_ID;   // compiled into the bundle at build time
let _staleBuildId = null;
let _pollTimer = null;
let _lastVisCheck = 0;
const _listeners = new Set();

const _fetchVersion = async () => {
  // Append a random cache-buster so even an aggressive CDN with stale rules
  // can't serve us the previous file.
  const url = `${VERSION_URL}?ts=${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  try {
    const res = await fetch(url, {
      cache: "no-store",
      credentials: "omit",
      headers: { "Cache-Control": "no-cache" },
    });
    if (!res.ok) return null;
    const data = await res.json();
    return typeof data?.buildId === "string" ? data.buildId : null;
  } catch (e) {
    return null;
  }
};

const _notify = () => {
  _listeners.forEach((fn) => { try { fn(_staleBuildId); } catch (e) { /* no-op */ } });
  try { window.dispatchEvent(new CustomEvent("opik:version-stale", { detail: { buildId: _staleBuildId } })); } catch (e) { /* no-op */ }
};

const _check = async () => {
  const latest = await _fetchVersion();
  if (!latest) return;
  // Compare the latest deployed buildId against the buildId compiled INTO
  // this JS bundle. If they differ, the user is on a stale build — even on
  // a "fresh" first load (CDN-cached HTML/JS scenario).
  if (!_runningBuildId) return; // dev build with no compiled BUILD_ID
  if (latest !== _runningBuildId && latest !== _staleBuildId) {
    _staleBuildId = latest;
    _notify();
  }
};

/** Subscribe to "new version available" events. Returns an unsubscribe fn. */
export const onVersionStale = (fn) => {
  _listeners.add(fn);
  // If already detected, fire immediately so late subscribers still see it.
  if (_staleBuildId) {
    try { fn(_staleBuildId); } catch (e) { /* no-op */ }
  }
  return () => _listeners.delete(fn);
};

/** Force-reload bypassing the HTTP cache by appending a random query string. */
export const hardReload = () => {
  if (typeof window === "undefined") return;
  const u = new URL(window.location.href);
  u.searchParams.set("v", Date.now().toString(36));
  window.location.replace(u.toString());
};

/**
 * Start polling. Called once from the app shell. Safe to call multiple times —
 * subsequent invocations are no-ops.
 */
export const startVersionWatcher = () => {
  if (typeof window === "undefined" || _pollTimer) return;
  _check();
  _pollTimer = setInterval(_check, POLL_MS);
  // Also re-check when the tab becomes visible after being backgrounded.
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState !== "visible") return;
    const now = Date.now();
    if (now - _lastVisCheck < VISIBILITY_DEBOUNCE_MS) return;
    _lastVisCheck = now;
    _check();
  });
};

export const getRunningBuildId = () => _runningBuildId;
